/**
 * Composition root. `services` is the singleton the app uses:
 *
 *   await services.ready;                       // storage open, demo seeded if empty, scheduler running
 *   services.app.bookAsClient({...})            // use cases (typed Results)
 *   services.storage.repo('clients').list(...)  // raw repos when a use case does not exist
 *   services.live(['appointments'], () => ...)  // useSyncExternalStore-ready live query
 *   services.demo.advance(24 * 3600_000)        // demo hooks
 */
import { createLinks, defaultBaseUrl, DEMO_SLUG, type Links } from '../config/env';
import { buildSupabaseConfig } from '../config/backend';
import { urlMode } from '../config/urlmode';
import { createSupabaseBackend } from './supabase';
import { resolveFlags, type Flags } from '../config/flags';
import { createUseCases, type UseCases } from '../domain/services';
import { getLocale } from '../i18n';
import { DAY, HOUR, MINUTE } from '../domain/time';
import { seed } from '../seed/seed';
import { createMockAuth } from './auth/mock';
import { createDemoClock } from './clock/demo';
import { createLocalKv, createMemoryKv } from './kv';
import { createLiveQuery, type LiveQuery } from './live';
import { createMockMessaging } from './messaging/mock';
import { createMockPayments } from './payments/mock';
import { createScheduler, type Scheduler } from './scheduler';
import { IndexedDbStorage } from './storage/indexeddb';
import { createMemoryStorage } from './storage/memory';
import type { AuthAdapter, Clock, KeyValueStore, MessagingAdapter, PaymentsAdapter, StorageAdapter, Unsubscribe } from './types';
import type { EntityName } from '../domain/types';

declare const __APP_BACKEND__: string | undefined;   // injected by scripts/build.mjs (esbuild define); undefined under node/tsx

export type DemoRole = 'owner' | 'customer';
export interface DemoControls {
  /** Slug of the seeded salon (`/b/smadar`). */
  readonly slug: string;
  /** Resets the clock to real time and reloads the seeded salon. */
  reset(): Promise<void>;
  /** Wipes everything: the empty-state demo (also signs out). */
  resetEmpty(): Promise<void>;
  advance(ms: number): void;
  advanceMinutes(n: number): void;
  advanceHours(n: number): void;
  advanceDays(n: number): void;
  setNow(ms: number): void;
  resetClock(): void;
  getRole(): DemoRole;
  setRole(role: DemoRole): void;
  subscribeRole(cb: () => void): Unsubscribe;
  /** The code the mock SMS would contain. */
  getDemoCode(phone: string): string | undefined;
}

export interface Services {
  flags: Flags;
  links: Links;
  kv: KeyValueStore;
  storage: StorageAdapter;
  messaging: MessagingAdapter;
  payments: PaymentsAdapter;
  auth: AuthAdapter;
  clock: Clock;
  scheduler: Scheduler;
  app: UseCases;
  demo: DemoControls;
  live<T>(entities: readonly EntityName[], load: () => Promise<T>): LiveQuery<T>;
  /** Live backend only: where the native app registers its push token. Undefined in the local demo. */
  devices?: LiveBackend['devices'];
  /** Resolves when storage is open, the demo is seeded (if empty) and the scheduler is running. */
  ready: Promise<void>;
}

/**
 * What a live (server-backed) backend must provide. The default demo never sets one. See src/adapters/supabase/index.ts.
 * `wrapUseCases` swaps server-side operations in; `start` begins session refresh and the change feed; there is no demo seeding or browser scheduler.
 */
export interface LiveBackend {
  kind: 'supabase';
  storage: StorageAdapter;
  messaging: MessagingAdapter;
  payments: PaymentsAdapter;
  auth: AuthAdapter;
  clock: Clock;
  wrapUseCases(app: UseCases): UseCases;
  /** Push token registration for the native app (see src/native/push.ts). */
  devices?: { register(token: string, platform: 'ios' | 'android'): Promise<boolean>; unregister(token: string): Promise<void> };
  start(): Promise<void>;
  stop(): void;
}

export interface CreateServicesOptions {
  /** Build a live backend instead of IndexedDB + mocks (APP_BACKEND=supabase). Unset = the local demo. */
  live?: (ctx: { kv: KeyValueStore; locale: typeof getLocale }) => LiveBackend;
  flags?: Flags;
  kv?: KeyValueStore;
  /** Real-time source for the demo clock (tests pass a fixed one). */
  realNow?: () => number;
  /** Do not seed or start timers (unit tests). */
  bare?: boolean;
  random?: () => number;
}

export function createServices(opts: CreateServicesOptions = {}): Services {
  const flags = opts.flags ?? resolveFlags();
  const kv = opts.kv ?? (flags.storage === 'memory' && !flags.autoStart ? createMemoryKv() : createLocalKv());
  const links = createLinks(defaultBaseUrl(), urlMode());
  const live = opts.live?.({ kv, locale: getLocale });
  const clock = live?.clock ?? createDemoClock({ kv, anchor: flags.anchorDemoClock === true, ...(opts.realNow ? { realNow: opts.realNow } : {}) });
  const storage: StorageAdapter = live?.storage ?? (flags.storage === 'indexeddb' ? IndexedDbStorage.create({ name: flags.dbName }) : createMemoryStorage({ ...(flags.autoStart ? { channelName: `planella-sync:memory:${flags.dbName}` } : {}) }));
  const messaging = live?.messaging ?? createMockMessaging({ storage, clock, locale: getLocale });
  const payments = live?.payments ?? createMockPayments({ storage, clock });
  const auth = live?.auth ?? createMockAuth({ kv, clock, ...(opts.random ? { random: opts.random } : {}) });
  const app = live ? live.wrapUseCases(createUseCases({ storage, messaging, payments, auth, clock, links, locale: getLocale })) : createUseCases({ storage, messaging, payments, auth, clock, links, locale: getLocale });
  const scheduler = createScheduler({ job: app.runScheduledJobs, clock, tickMs: flags.tickMs });
  payments.onStatus(() => void scheduler.run().catch(() => undefined));

  const roleListeners = new Set<() => void>();
  const load = async (empty: boolean): Promise<void> => {
    clock.reset();
    auth.signOut();
    await storage.reset(empty ? undefined : seed({ now: clock.now(), baseUrl: defaultBaseUrl() }).data);
    await scheduler.run();
  };
  const demo: DemoControls = {
    slug: DEMO_SLUG,
    reset: () => load(false),
    resetEmpty: () => load(true),
    advance: (ms) => clock.advance(ms),
    advanceMinutes: (n) => clock.advance(n * MINUTE),
    advanceHours: (n) => clock.advance(n * HOUR),
    advanceDays: (n) => clock.advance(n * DAY),
    setNow: (ms) => clock.setNow(ms),
    resetClock: () => clock.reset(),
    getRole: () => (kv.get('demo.role') === 'customer' ? 'customer' : 'owner'),
    setRole(role) { kv.set('demo.role', role); for (const cb of [...roleListeners]) cb(); },
    subscribeRole(cb) { roleListeners.add(cb); return () => void roleListeners.delete(cb); },
    getDemoCode: (phone) => auth.getDemoCode(phone),
  };

  const ready = opts.bare ? Promise.resolve() : live ? live.start() : (async (): Promise<void> => {
    const businesses = await storage.repo('businesses').list();
    // A demo left over from an earlier day would show past data around a clock that restarted: refresh it (only when it is purely the demo).
    const stale = flags.anchorDemoClock === true && businesses.length > 0 && businesses.every((b) => b.isDemo)
      && (() => { const at = clock.anchoredAt?.(); return at === undefined || (opts.realNow ? opts.realNow() : Date.now()) - at > 8 * HOUR; })();
    if (flags.seedDemo && stale) { clock.reset(); await storage.reset(seed({ now: clock.now(), baseUrl: defaultBaseUrl() }).data); }
    else if (flags.seedDemo && businesses.length === 0) {
      await storage.reset(seed({ now: clock.now(), baseUrl: defaultBaseUrl() }).data);
    }
    await scheduler.run();
    if (flags.autoStart) scheduler.start();
  })();

  return {
    flags, links, kv, storage, messaging, payments, auth, clock, scheduler, app, demo, ready,
    live: (entities, loader) => createLiveQuery(storage, entities, loader, [clock.subscribe]),
    ...(live?.devices ? { devices: live.devices } : {}),
  };
}

/** The app-wide singleton (IndexedDB + mocks; `?memory=1` forces memory). */
export const services: Services = createServices(
  // BUILD_BACKEND is a build-time constant: in the default (local) build this branch and the whole Supabase adapter tree are dead code.
  typeof __APP_BACKEND__ !== 'undefined' && __APP_BACKEND__ === 'supabase' ? { live: ({ kv, locale }) => createSupabaseBackend(buildSupabaseConfig(), { kv, locale }) } : {},
);
